/**
 * @module ConfirmDialog
 * @purpose Generic reusable confirmation dialog with focus-trap, keyboard and backdrop dismiss.
 * @depends React (useEffect, useRef, useCallback)
 * @dependedOnBy CanvasReasoningPanel (force-recompute), any future confirmation flows
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none — controlled entirely by caller via open prop
 * - Errors: n/a
 * - Theming: design tokens only; no Tailwind colour utilities
 * - Note: no --danger token exists yet; destructive confirm button uses var(--accent) as fallback
 */

import { type ReactNode, useCallback, useEffect, useRef } from 'react'

const PANEL_MAX_WIDTH = '480px'
const Z_INDEX_DIALOG = 50
const BACKDROP = 'rgba(0,0,0,0.45)'
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: Z_INDEX_DIALOG,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: BACKDROP,
}

const dialogStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '24px',
  width: '100%',
  maxWidth: PANEL_MAX_WIDTH,
  outline: 'none',
}

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '12px',
}

const bodyStyle: React.CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: '0.875rem',
  marginBottom: '24px',
}

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
}

const buttonBaseStyle: React.CSSProperties = {
  borderRadius: '6px',
  padding: '8px 16px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

const confirmButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}

const cancelButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
}

export interface ConfirmDialogProps {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  destructive?: boolean
}

function wrapTabFocus(event: KeyboardEvent, dialog: HTMLDivElement): void {
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  const first = focusable.at(0)
  const last = focusable.at(-1)
  if (first === undefined || last === undefined) return
  const active = document.activeElement
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

function useFocusTrap(
  open: boolean,
  dialogRef: React.RefObject<HTMLDivElement>,
  onCancel: () => void,
): void {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
        return
      }
      if (event.key === 'Tab' && dialogRef.current) {
        wrapTabFocus(event, dialogRef.current)
      }
    },
    [onCancel, dialogRef],
  )

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement as HTMLElement
    dialogRef.current?.focus()
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [open, handleKeyDown, dialogRef])
}

/**
 * Reusable confirmation dialog with focus-trap, Escape-to-cancel, and optional backdrop dismiss.
 *
 * @behaviour When open: traps focus inside dialog; Escape calls onCancel.
 *            Backdrop click calls onCancel only when destructive=false.
 *            Labels are caller-supplied (translated at call site).
 * @errors n/a — purely presentational
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  destructive = false,
}: ConfirmDialogProps): React.ReactElement | null {
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(open, dialogRef, onCancel)

  if (!open) return null

  return (
    <div
      role="presentation"
      style={overlayStyle}
      onClick={destructive ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        ref={dialogRef}
        tabIndex={-1}
        style={dialogStyle}
        onClick={(ev) => { ev.stopPropagation() }}
      >
        <h2 id="confirm-dialog-title" style={titleStyle}>{title}</h2>
        <div style={bodyStyle}>{body}</div>
        <div style={footerStyle}>
          <button type="button" style={cancelButtonStyle} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" style={confirmButtonStyle} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
